import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SealCheck, ArrowRight, X } from "@phosphor-icons/react";

/**
 * VerificationUpgradeNudge — 30-day-snoozable card that surfaces to
 * candidates who are KYC-verified (free) but haven't upgraded to the
 * ₹299 Paid Verified Badge.
 *
 * V1.5.8.16 — behavioural rule (chose option B from user):
 *   • Shown ONCE every 30 days if the candidate is still on the KYC tier.
 *   • Dismissing writes a 30-day timestamp to localStorage so we don't
 *     re-nag on next page load.
 *   • Server truth: hits `/me/verified-badge/status` on mount to read `tier`.
 *     If tier flips to "paid", the card self-hides even if the dismiss
 *     window isn't up.
 */
const STORAGE_KEY = "nd_upsell_paid_verified_dismissed_at";
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;   // 30 days

function isSnoozed() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        return Number.isFinite(ts) && (Date.now() - ts) < SNOOZE_MS;
    } catch { return false; }
}

export default function VerificationUpgradeNudge() {
    const [tier, setTier] = useState(null);
    const [dismissed, setDismissed] = useState(() => isSnoozed());
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/me/verified-badge/status")
            .then((r) => { if (alive) { setTier(r.data?.tier || null); setLoaded(true); } })
            .catch(() => { if (alive) setLoaded(true); });
        return () => { alive = false; };
    }, []);

    if (!loaded || dismissed || tier !== "kyc") return null;

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
        setDismissed(true);
    };

    return (
        <div
            data-testid="dashboard-upgrade-nudge"
            className="mb-6 relative overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-cobalt/5 p-4 sm:p-5"
        >
            <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-emerald-200/40 blur-2xl pointer-events-none" aria-hidden />
            <button
                onClick={dismiss}
                aria-label="Dismiss for 30 days"
                data-testid="dashboard-nudge-dismiss"
                className="absolute top-2 right-2 text-[#A3A3A3] hover:text-ink p-1"
            >
                <X size={16} weight="bold" />
            </button>
            <div className="relative flex items-start gap-3">
                <SealCheck size={24} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 pr-6">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 mb-1">
                        You&apos;re KYC-verified · rank higher
                    </p>
                    <p className="font-heading text-lg sm:text-xl font-black tracking-tight text-ink leading-tight">
                        Paid Verified badges rank <span className="text-cobalt">first</span> in shortlists.
                    </p>
                    <p className="text-xs sm:text-sm text-[#525252] mt-1.5">
                        One-time ₹299. Blue-check on your profile, priority in agency filters, faster shortlisting.
                    </p>
                    <Link
                        to="/candidate/verified-badge"
                        data-testid="dashboard-nudge-cta"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-cobalt hover:underline"
                    >
                        Upgrade to Paid Verified <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
